import { FPError, Suggest, Filter, HotelOffers } from 'types';
import i18n from '../../plugins/i18n';
import './../../extensions/selectable';
import { MapTypes } from './map';
import mapboxgl from 'mapbox-gl';

export namespace Search {
  export const limitPerPage = 60;

  export enum SearchStateTypes {
    IDLE = 'idle',
    STARTED = 'started',
    FAILED = 'failed',
    SUCCESS = 'success',
  }
  export enum SearchByTypes {
    NONE,
    SUGGEST,
    GEO,
  }

  export enum ViewTypes {
    LIST,
    MAP,
  }
  export enum MapEvent {
    ZOOM,
    DRAGEND,
  }

  export class SearchState {
    state: SearchStateTypes = SearchStateTypes.IDLE;
    suggestState: SearchStateTypes = SearchStateTypes.IDLE;
    searchBy: SearchByTypes = SearchByTypes.NONE;
    error: FPError = null;

    initLoading: boolean = false;
    fetchLoading: boolean = false;
    mapLoading: boolean = false;

    suggestLoading: boolean = false;
    route: any = null;
    view: ViewTypes = ViewTypes.LIST;
    positionOnList: number = 0;
    searchStartTime: number = 0;
    searchFinishedTime: number = 0;

    reset() {
      this.state = SearchStateTypes.IDLE;
      this.suggestState = SearchStateTypes.IDLE;
      this.error = null;
      this.route = null;
      this.positionOnList = 0;
      this.suggestLoading = false;
      this.searchStartTime = 0;
      this.searchFinishedTime = 0;
      this.mapLoading = false;
    }

    isSuccess() {
      return this.state === SearchStateTypes.SUCCESS;
    }

    isFailed() {
      return this.state === SearchStateTypes.FAILED;
    }

    process() {
      return this.state === SearchStateTypes.STARTED;
    }

    suggestIsFinished() {
      return (
        this.suggestState === SearchStateTypes.SUCCESS ||
        this.suggestState === SearchStateTypes.FAILED
      );
    }

    progressShowed() {
      return this.initLoading || this.fetchLoading;
    }

    onMap() {
      return this.view === ViewTypes.MAP;
    }

    byMap() {
      return this.searchBy === SearchByTypes.GEO;
    }

    hasSavedState() {
      return this.isSuccess() && this.route;
    }

    errorHeader() {
      switch (this.error.header) {
        case 'NO_RESULTS':
          return i18n.global.t('errors.search.NO_RESULTS_HEADER');
        default:
          return i18n.global.t('errors.search.DEFAULT_HEADER');
      }
    }

    errorMessage(destination?: string) {
      switch (this.error.header) {
        case 'NO_RESULTS':
          return i18n.global.t('errors.search.NO_RESULTS_MESSAGE', {
            destination,
          });
        default:
          return i18n.global.t('errors.NETWORK_ERROR');
      }
    }

    isButtonEnableOnError() {
      switch (this.error.header) {
        case 'NO_RESULTS':
          return true;
        default:
          return true;
      }
    }
  }

  export interface ISearchState {
    state: SearchStateTypes;
    initLoading?: boolean;
    mapLoading?: boolean;
    fetchLoading?: boolean;
    error?: FPError;
  }

  export interface ISearchUIState {
    route?: any;
    position?: number;
  }

  export enum MarkerType {
    Label = 'label',
    Pin = 'pin',
  }

  export interface Marker {
    id: number | string;
    markerElement: HTMLDivElement | any;
    price: number | string;
    type: MarkerType;
    preview: boolean;
    instance?: mapboxgl.Marker;
  }

  export interface FocusInterface {
    focus?: boolean;
    id: number;
    isMarker?: boolean;
  }

  export interface Static {
    page: number;
    model: SearchPulling;
    searchId: string;
  }

  export interface SearchPulling {
    status: string;
    search_id: string;
    hotel_ids: number[];
    chunk_size: number;
    show_on_list?: number[];
  }

  export interface DynamicSearch {
    status: string;
    result: DynamicSearchResult;
  }

  export interface DynamicSearchResult {
    show_on_list: number[];
  }
  export interface AsyncSearchRequestBody {
    id: string;
    checkin: string;
    checkout: string;
    adults: number;
    children: number;
  }

  export interface AsyncSearchResponse {
    status: string;
    search_id: string;
    fast_search_id: string;
  }

  export interface HotelSearchResponse {
    status: string;
    result?: HotelSearchResult;
    search_id: string;
  }

  export interface HotelSearchResult {
    hotels: Hotel[];
    show_on_map?: number[];
    show_on_list: number[];
    amenities?: Amenity[];
    filters?: HotelOffers.FilterSection[];
    featured: Amenity[];
    total_hotels?: number;
    search_price_token: string;
    request_id: string;
  }

  export interface HotelsStaticResponse {
    hotels: StaticHotel[];
    amenities?: Amenity[];
    featured: Amenity[];
  }

  export interface StaticResponseWithSearchId {
    searchId: string;
    res: HotelsStaticResponse;
  }

  export interface StaticHotel {
    amenities?: number[];
    featured: number[];
    hotel_chain?: string;
    fp_hotel_score?: number;
    id: string;
    location: HotelOffers.Location;
    promo_text?: string;
    rating?: StaticHotelRating;
    stars: number;
    thumb?: string;
    thumb_preview?: string;
    reviews_count: number;
    title: string;
    website?: string;
    publication_score?: number;
    publications_count?: number;
    summary?: string;
    rating_city?: number;
    prices_color?: string;
    hotel_type?: string;
    bg_color?: string;
    is_genius?: HotelOffers.BookingGenius;
    reward_program: string;
  }

  export interface StaticHotelRating {
    rating: number;
    ratingCount: number;
    ratingText: string;
  }

  export interface Hotel {
    amenities?: string[];
    featured: string[];
    fp_hotel_score?: number;
    currency: string;
    title?: string;
    id: number | string;
    location: HotelOffers.Location;
    options?: string[];
    prices?: Price[];
    prices_status?: string;
    promo_text?: string;
    rating: HotelOffers.Rating;
    stars: number;
    thumb?: string;
    thumb_preview?: string;
    themes: string[];
    reviews_count: number;
    offer_info?: string[];
    hotel_chain?: string;
    website?: string;
    publication_score?: number;
    publications_count?: number;
    summary?: string;
    rating_city?: number;
    prices_color?: string;
    hotel_type?: string;
    bg_color?: string;
    filter_ref: number[];
    is_genius: HotelOffers.BookingGenius;
    reward_program: string;
  }

  export interface HotelAmenitiesEntity {
    list: Amenity[];
    featured: Amenity[];
  }

  export interface Amenity {
    id: string;
    title: string;
  }

  export enum Supplier {
    BOOKING = 'Booking.com',
    EXPEDIA = 'Expedia.com',
    TRAVELPORT = 'Travelport',
  }

  export enum HotelSorting {
    RECOMMENDED,
    REVIEWS,
    PRICE,
    SAVINGS,
  }

  export interface Location {
    city: string;
    country: string;
    state: string;
    address: string;
  }

  export interface InfoWindowModel {
    title: string;
    rating: string;
    reviews: string;
    stars: string;
    byMap: boolean;
    prices: HotelItemPricesModel;
  }
  export interface HotelItemPricesModel {
    status: PriceStatus;
    price?: string;
    savings?: string;
    publicPrice?: string;
    discount?: string;
    marketPrice?: string;

    priceAsNumber?: number;
    savingsAsNumber?: number;
  }

  export class HotelResultModel {
    id: number | string;
    stars: number;
    title: string;
    offerOptionsInfo: string;
    location: Location;
    district: string;
    coordinate: CoordinateModel;
    priceStatus: string;
    price?: HotelPriceModel[];
    rating: RatingModel;
    options: HotelOffers.OfferFilter[];
    featured: HotelAmenityModel[];
    thumb?: string;
    themes: string[];
    fpScore: number;
    distanceFrom?: number;
    promoText: string;
    preview: boolean;
    thumb_preview?: string;
    priceStatusIndex: number;
    chain?: string;
    website?: string;
    publication_score?: number;
    publications_count?: number;
    summary?: string;
    cityRating?: number;
    prices_color?: string;
    byMap?: boolean;
    hotel_type?: string;
    bg_color?: string;
    filter_ref?: number[];
    is_genius?: HotelOffers.BookingGenius;
    reward_program?: string;
  }

  export class CoordinateModel {
    lat: number;
    lng: number;

    constructor(lat: number, lng: number) {
      this.lat = lat;
      this.lng = lng;
    }

    toMapBoxSystem(): MapTypes.LngLatInput {
      return [this.lng, this.lat];
    }
  }

  export class HotelPriceModel {
    token?: string;
    options: string[];
    prepay: boolean;
    marketPrice: boolean;
    // total: number;
    price: number;
    saving: number;
    discount: number;
    cashback: number;
    publicPrice: number;
    filter_ref: number[];
    supplier?: Supplier;
    public?: boolean;

    constructor(price: Search.Price) {
      this.token = price.token;
      this.options = price.options;
      this.prepay = price.prepay;
      this.marketPrice = price.market_price;
      this.price = price.price;
      this.discount = price.discount;
      this.saving = price.saving;
      this.cashback = price.cashback;
      // this.total = price.price;
      this.publicPrice = price.public_price ? price.public_price : 0;
      this.filter_ref = price.filter_ref;
      this.supplier = price.supplier;
      this.public = price.public;
    }
  }

  export class RatingModel {
    rating: number;
    explanation: string;
    reviewsCount: number;

    constructor(rating: HotelOffers.Rating, reviewsCount: number) {
      this.rating = rating.count;
      this.explanation = rating.title;
      this.reviewsCount = reviewsCount ? reviewsCount : 0;
    }
  }

  export class HotelAmenityModel {
    id: string;
    name: string;
    active: boolean;

    constructor(id: string, name: string, active: boolean) {
      this.id = id;
      this.name = name;
      this.active = active;
    }
  }

  export class HotelAmenitiesFactory {
    static compose(
      all: Amenity[],
      modelAmenities: string[],
    ): HotelAmenityModel[] {
      return all
        .filter(amenity => modelAmenities.includes(amenity.id))
        .map(
          amenity =>
            new HotelAmenityModel(
              amenity.id,
              amenity.title,
              modelAmenities.includes(amenity.id),
            ),
        );
    }

    static composeDetails(modelAmenities: string[]): HotelAmenityModel[] {
      return Filter.facilitiesIds
        .map(id => {
          return {
            id: id,
            title: i18n.global.t(`filters.ammenites.${id}`),
          };
        })
        .map(
          amenity =>
            new HotelAmenityModel(
              amenity.id,
              amenity.title,
              modelAmenities.includes(amenity.id),
            ),
        );
    }

    static icon(id: string) {
      switch (id) {
        // case 50 : return true;
        case 'FP':
          return true; // 49
        case 'PL':
          return true; // 62
        case 'FC':
          return true; // 48
        case 'SP':
          return true; // 60
        // case 1 : return true; // 1
        case 'RS':
          return true; // 133
        // case 294 : return true; // 294
        case 'RT':
          return true; // 93
        case 'BL':
          return true; // 149
        case 'CA':
          return true; // 8
        case 'BC':
          return true; // 229
        case 'PA':
          return true; // 88
        case 'SL':
          return true; // 191
        default:
          return false;
      }
    }
  }

  export interface SearchResultsV2 {
    'room-id': string;
    'response-id': string;
    sse: boolean;
    status: string;
    data: SearchResultsData;
  }

  export interface SearchResultsData {
    rewardPrograms: string[];
    hotels: HotelDetails[];
    total: number;
    totalProcessed: number;
    from: number;
    howMany: number;
    expirationTime?: string;
  }

  export enum PriceStatus {
    Success = 'success',
    Soldout = 'soldout',
    Filtered = 'filtered',
    Loading = 'loading',
  }
  export interface HotelDetails {
    _id: number;
    id: string;
    price?: {
      status?: PriceStatus;
      price?: number;
      effectivePrice?: number;
      strikethrough?: number;
      save?: number;
      savePercent?: number;
      cashback?: number;
      cashbackPercent?: number;
      provider?: string;
      currency?: string;
      effectivePriceForNight?: number;
    };
    name?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    label?: string;
    thumb?: string;
    stars?: number;
    rating?: number;
    hotel?: boolean;
    selected: boolean;
    accommodationType?: string;
    summary?: string;
    bgColor?: string;
    rewardProgram?: string;
    url?: string;
    isGenius?: boolean;
    priceModel?: HotelItemPricesModel;
  }

  export interface SearchRouteParams {
    id: string;
    in?: string;
    out?: string;
    guests?: string;
    repeat?: string;
    destintaion?: string;
    hotelId?: string;
    hotel?: string;
    type?: string;
    city?: string;
    state?: string;
    country?: string;
    filters?: Filter.Query;
    currency?: string;
    locale?: string;
  }

  export namespace HotelCategorySurvey {
    const KEY = 'survey_hotel_category_selection';

    export interface IPayload {
      value: Filter.StarsType[];
      isSurveyShowed: boolean;
      timestamp: string;
    }

    export function get(): IPayload | null {
      try {
        const stored = localStorage.getItem(KEY);
        if (stored == null) {
          throw new Error();
        }

        const parsed = JSON.parse(stored);

        if (parsed && 'value' in parsed) {
          return parsed as IPayload;
        }

        return null;
      } catch (e) {
        return null;
      }
    }

    export function save(value: Filter.StarsType[], isSurvey: boolean = false) {
      const stored = get();
      const timestamp = new Date().toISOString();

      const json: IPayload = {
        value,
        timestamp,
        isSurveyShowed: stored?.isSurveyShowed || isSurvey,
      };
      localStorage.setItem(KEY, JSON.stringify(json));
    }

    export function clear() {
      localStorage.removeItem(KEY);
    }
  }
}
